import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, ChevronDown, ChevronRight, Tag } from 'lucide-react';

import { cn } from '@/lib/cn';
import { fmtInt, fmtUsd } from '@/lib/format';
import type { WineGroup } from './GroupedWineCard';
import type { Wine } from './types';

/**
 * Tabla desktop con vinos agrupados por (bodega, nombre, formato).
 * Cada fila principal es el "vino genérico"; al expandirla se ven las añadas
 * como sub-filas linkeables al detalle del vino específico.
 *
 * Se le pasa el array original de wines para poder resolver metadata por añada
 * (fotos, valor unitario, etc) que WineGroup no guarda.
 */
export function GroupedWineTable({
  groups,
  wines,
  onToggleKeeper,
  keeperPending,
}: {
  groups: WineGroup[];
  wines: Wine[];
  onToggleKeeper: (id: number, current: boolean) => void;
  keeperPending?: boolean;
}) {
  // Map id → Wine para lookups rápidos en las sub-filas.
  const byId = new Map<number, Wine>();
  wines.forEach((w) => byId.set(w.id, w));

  return (
    <div className="card overflow-x-auto hidden md:block">
      <table className="table-base min-w-[960px]">
        <thead className="bg-white">
          <tr>
            <th className="w-8"></th>
            <th>Vino</th>
            <th>Bodega</th>
            <th>Añadas</th>
            <th>Tipo</th>
            <th>Formato</th>
            <th>Stock total</th>
            <th className="text-center">Fotos</th>
            <th>Valor est. USD</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <GroupRow
              key={g.key}
              group={g}
              byId={byId}
              onToggleKeeper={onToggleKeeper}
              keeperPending={keeperPending}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRow({
  group,
  byId,
  onToggleKeeper,
  keeperPending,
}: {
  group: WineGroup;
  byId: Map<number, Wine>;
  onToggleKeeper: (id: number, current: boolean) => void;
  keeperPending?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasMultiple = group.years.length > 1;

  // Referencia al primer wine del grupo (para tipo/type_label) — todos los wines
  // de un grupo tienen el mismo tipo por definición del key de agrupamiento.
  const first = byId.get(group.years[0]?.wine_id);
  const totalPhotos = group.years.reduce(
    (s, y) => s + (byId.get(y.wine_id)?.images_count ?? 0),
    0,
  );

  return (
    <>
      <tr
        className={cn(
          'hover:bg-white',
          hasMultiple ? 'cursor-pointer' : '',
          expanded && 'bg-notion-50/60',
        )}
        onClick={() => hasMultiple && setExpanded((v) => !v)}
      >
        <td className="text-center">
          {hasMultiple ? (
            expanded ? (
              <ChevronDown size={14} className="text-notion-500 inline" />
            ) : (
              <ChevronRight size={14} className="text-notion-500 inline" />
            )
          ) : (
            <span className="text-notion-300 text-xs">·</span>
          )}
        </td>
        <td>
          {hasMultiple ? (
            <span className="font-medium text-notion-800">{group.name}</span>
          ) : (
            <Link
              to={`/wines/${group.years[0].wine_id}`}
              className="font-medium text-notion-800 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {group.name}
            </Link>
          )}
          {group.grape_summary && (
            <p className="text-xs text-notion-500 mt-0.5">{group.grape_summary}</p>
          )}
        </td>
        <td>{group.winery_name ?? '—'}</td>
        <td>
          <div className="flex flex-wrap gap-1">
            {group.years.map((y) => (
              <span
                key={y.wine_id}
                className={cn(
                  'text-[11px] tabular-nums px-1.5 py-0.5 rounded border',
                  y.is_keeper
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800',
                )}
                title={`${y.vintage ?? 's/a'} · ${y.stock} bot`}
              >
                {y.vintage ?? 's/a'}
                <span className="text-notion-500 ml-0.5">·{fmtInt(y.stock)}</span>
              </span>
            ))}
          </div>
        </td>
        <td>
          <span className="capitalize">{first?.type_label ?? '—'}</span>
        </td>
        <td>
          <span className="text-xs text-notion-600">{group.format}</span>
        </td>
        <td>
          <strong className="text-notion-800">{fmtInt(group.total_stock)}</strong>
        </td>
        <td className="text-center">
          {totalPhotos > 0 ? (
            <span
              className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-notion-50 border border-notion-200 text-notion-700"
              title={`${totalPhotos} foto${totalPhotos === 1 ? '' : 's'}`}
            >
              <Camera size={11} />
              {totalPhotos}
            </span>
          ) : (
            <span className="text-notion-300 text-xs">—</span>
          )}
        </td>
        <td>
          {group.estimated_value_total_usd > 0
            ? fmtUsd(group.estimated_value_total_usd)
            : '—'}
        </td>
      </tr>

      {/* Sub-filas por añada — solo cuando el grupo está expandido */}
      {expanded &&
        group.years.map((y) => {
          const w = byId.get(y.wine_id);
          if (!w) return null;
          return (
            <tr key={`sub-${y.wine_id}`} className="bg-notion-50/40">
              <td></td>
              <td colSpan={2}>
                <Link
                  to={`/wines/${y.wine_id}`}
                  className="text-notion-700 hover:underline pl-6 inline-flex items-center gap-2"
                >
                  <span className="text-notion-400 text-xs">↳</span>
                  Añada {y.vintage ?? 's/a'}
                </Link>
              </td>
              <td colSpan={2}>
                <span className="text-xs text-notion-500">
                  {w.grape_varieties?.map((gv) => gv.name).join(' · ') || '—'}
                </span>
              </td>
              <td></td>
              <td>{fmtInt(y.stock)}</td>
              <td className="text-center">
                {(w.images_count ?? 0) > 0 ? (
                  <span className="text-xs text-notion-600">{w.images_count}</span>
                ) : (
                  <span className="text-notion-300 text-xs">—</span>
                )}
              </td>
              <td>
                <div className="flex items-center justify-between gap-2">
                  <span>{fmtUsd(y.estimated_value_usd ?? null)}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleKeeper(y.wine_id, y.is_keeper);
                    }}
                    disabled={keeperPending}
                    className={cn(
                      'inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border',
                      y.is_keeper
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100'
                        : 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100',
                      keeperPending && 'opacity-50',
                    )}
                    title={y.is_keeper ? 'Guarda — clic para pasar a En venta' : 'En venta — clic para pasar a Guarda'}
                  >
                    <Tag size={10} />
                    {y.is_keeper ? 'Guarda' : 'En venta'}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
    </>
  );
}
